import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiFetch, apiUrl, buildQueryString } from "./client";
import type {
  CreateTemplateRequest,
  Template,
  TemplateListParams,
  TemplateShare,
  TemplateVersion,
  UpdateTemplateRequest,
} from "@/types/template";

export const templateKeys = {
  all: ["templates"] as const,
  list: (params: TemplateListParams) => ["templates", "list", params] as const,
  detail: (id: string) => ["templates", id] as const,
  versions: (id: string) => ["templates", id, "versions"] as const,
  shares: (id: string) => ["templates", id, "shares"] as const,
};

export function useTemplatesQuery(params: TemplateListParams = {}) {
  return useQuery({
    queryKey: templateKeys.list(params),
    queryFn: () =>
      apiFetch<Template[]>(
        `/templates${buildQueryString(params as Record<string, string | number | boolean | undefined>)}`,
      ),
  });
}

export function useTemplateByIdQuery(id: string) {
  return useQuery({
    queryKey: templateKeys.detail(id),
    queryFn: () => apiFetch<Template>(`/templates/${id}`),
    enabled: !!id,
  });
}

export function useTemplateVersionsQuery(id: string) {
  return useQuery({
    queryKey: templateKeys.versions(id),
    queryFn: () => apiFetch<TemplateVersion[]>(`/templates/${id}/versions`),
    enabled: !!id,
  });
}

export function useCreateTemplateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTemplateRequest) =>
      apiFetch<Template>("/templates", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

export function useUpdateTemplateMutation(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateTemplateRequest) =>
      apiFetch<Template>(`/templates/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(templateKeys.detail(id), updated);
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

export function useDeleteTemplateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/templates/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

/**
 * POST a captured PNG to /api/templates/:id/thumbnail. The body is raw
 * image bytes (Content-Type: image/png) — not JSON — so we hit fetch
 * directly instead of going through apiFetch which assumes JSON.
 */
export function useSetThumbnailMutation(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pngBlob: Blob) => {
      const { useAuthStore } = await import("@/stores/auth");
      const token = useAuthStore.getState().token;
      const res = await fetch(apiUrl(`/templates/${id}/thumbnail`), {
        method: "POST",
        headers: {
          "Content-Type": "image/png",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: pngBlob,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`thumbnail upload failed: ${res.status} ${text}`);
      }
      return (await res.json()) as { url: string | null };
    },
    onSuccess: () => {
      // Bumps the template's updated_at on next list/detail fetch so the
      // ?v= cache-bust on <img> tags forces the browser to fetch the new
      // PNG. Without this the cards keep showing the prior version.
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

/**
 * Compute the URL to the template's thumbnail PNG, with a cache-busting
 * query param keyed on updated_at. Returns null when the template has
 * never had a thumbnail set — caller falls back to the placeholder.
 *
 * The frontend can't peek at templates_dir to know whether thumbnail.png
 * exists, so we encode the answer in the template metadata: when a
 * thumbnail has been uploaded, the backend's update_at bumps and the
 * image becomes available. We optimistically try the URL when the
 * template has any updated_at — the <img> simply 404s gracefully into
 * the onError fallback if no file exists.
 */
export function templateThumbnailUrl(template: { id: string; updated_at?: string }): string {
  const v = template.updated_at
    ? encodeURIComponent(template.updated_at)
    : "0";
  return apiUrl(`/templates/${template.id}/thumbnail.png?v=${v}`);
}

export function useCloneTemplateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<Template>(`/templates/${id}/clone`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

export function useImportTemplateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: unknown) =>
      apiFetch<Template>("/templates/import", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

export function exportTemplateUrl(id: string): string {
  return apiUrl(`/templates/${id}/export`);
}

// ── Visibility ──────────────────────────────────────────────────────────

export function useToggleVisibilityMutation(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (isPublic: boolean) =>
      apiFetch<{ is_public: boolean }>(`/templates/${id}/visibility`, {
        method: "PUT",
        body: JSON.stringify({ is_public: isPublic }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

// ── Sharing ─────────────────────────────────────────────────────────────

export function useTemplateSharesQuery(id: string) {
  return useQuery({
    queryKey: templateKeys.shares(id),
    queryFn: () => apiFetch<TemplateShare[]>(`/templates/${id}/shares`),
    enabled: !!id,
  });
}

export function useShareTemplateMutation(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (username: string) =>
      apiFetch<{ shared: boolean; user_id: string }>(`/templates/${id}/share`, {
        method: "POST",
        body: JSON.stringify({ username }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.shares(id) });
    },
  });
}

export function useUnshareTemplateMutation(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch<{ unshared: boolean }>(`/templates/${id}/share/${userId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.shares(id) });
    },
  });
}
